import type { FC } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';
import QuantityBox from './QuantityBox';
import CartItemButtons from './CartItemButtons';
import Price from './Price';
import type { CourseDto, MenuItemVariantDto } from '../../../../../../generated-api';
import { getComponentKey } from '../../../../../utils/orderLineUtils';
import classNames from 'clsx';
import { useAllMenuSections, useMenuCourses } from '../../../../../context/MenuSectionsStore';
import { useItemWeightDisplay } from '../../../../../hooks/useItemWeightDisplay';
import CartItemDetails from './CartItemDetails';
import { useAllProducts } from '../../../../../hooks/useAllProducts';
import type { CartOrderLine, CartOrderLineComponent } from '../../../../../../types/cart';

interface ComponentTreeItemProps {
  component: CartOrderLineComponent;
  courses: CourseDto[];
  findMenuItem: (itemVariantId: string) => MenuItemVariantDto | undefined;
  giftCardPhoneNumber?: string;
  level?: number;
}

/**
 * Recursive component for rendering a component and its children with depth-based indentation.
 * Each level wraps in pl-4 so nesting naturally compounds.
 */
const ComponentTreeItem: FC<ComponentTreeItemProps> = ({
  component,
  courses,
  findMenuItem,
  giftCardPhoneNumber,
  level = 0,
}) => {
  const componentMenuItem = findMenuItem(component.itemVariantId);
  const courseName = courses.find((c) => c.courseId === component.courseId)?.name;

  const orderLineForWeight = useMemo(
    () => ({
      openPrice: false,
      quantity: 1,
      unitQuantity: component.unitQuantity,
    }),
    [component.unitQuantity]
  );

  const { hasWeight, displayQuantity, unitLabel } = useItemWeightDisplay(orderLineForWeight, componentMenuItem);
  const children = component.components ?? [];

  const indentationClass = `pl-${level * 4}`; // Tailwind doesn't support dynamic values, so we calculate the class name here

  return (
    <div className={`flex flex-col ${indentationClass}`}>
      <div className="flex justify-between items-end">
        <CartItemDetails
          name={component.name}
          quantity={component.unitQuantity}
          hasWeight={hasWeight}
          displayQuantity={displayQuantity}
          unitLabel={unitLabel}
          options={component.options}
          giftCardPhoneNumber={giftCardPhoneNumber}
          comment={component.comment}
        />
        {courseName && <span className="text-grey-dark text-xs shrink-0">{courseName}</span>}
      </div>
      {children.map((child) => (
        <ComponentTreeItem
          key={getComponentKey(child)}
          component={child}
          courses={courses}
          findMenuItem={findMenuItem}
          giftCardPhoneNumber={giftCardPhoneNumber}
          level={level + 1}
        />
      ))}
    </div>
  );
};

interface Props {
  orderLine: CartOrderLine;
  itemIndex: number;
  setActiveItem: (index?: number) => void;
  isActive: boolean;
  giftCardPhoneNumber?: string;
  className?: string;
}

const CompositeCartItem: FC<Props> = ({
  orderLine,
  itemIndex,
  setActiveItem,
  isActive,
  giftCardPhoneNumber,
  className,
}) => {
  const ref = useRef<HTMLLIElement>(null);
  const allMenuSections = useAllMenuSections();
  const allProducts = useAllProducts();
  const courses = useMenuCourses();

  // Get component items directly from the nested array
  const componentItems = orderLine.components ?? [];

  const handlePressItem = useCallback(() => {
    setActiveItem(itemIndex);
  }, [setActiveItem, itemIndex]);

  const liClassName = useMemo(
    () =>
      classNames('py-2', className, {
        'bg-purple-lightest': isActive,
      }),
    [className, isActive]
  );

  // Find menu item for parent composite product
  const menuItem = useMemo(() => {
    if (!orderLine.unitQuantity || !orderLine.openPrice) {
      return null;
    }
    return allMenuSections
      .flatMap((section) => section.items)
      .find((item) => item.variantLinkId === orderLine.itemVariantId);
  }, [allMenuSections, orderLine.itemVariantId, orderLine.unitQuantity, orderLine.openPrice]);

  const findMenuItem = useCallback(
    (componentItemVariantId: string) => allProducts.find((item) => item.variantLinkId === componentItemVariantId),
    [allProducts]
  );

  // Weight display for parent item
  const { hasWeight, displayQuantity, unitLabel } = useItemWeightDisplay(orderLine, menuItem);

  return (
    <li
      ref={ref}
      className={liClassName}
      role="button"
      tabIndex={0}
      aria-label="Change quantity"
      onClick={handlePressItem}
    >
      <div className="grid grid-cols-[auto_1fr_auto] gap-x-2 gap-y-2 items-baseline" data-cy="cart-line ">
        <QuantityBox isActive={isActive} orderLine={orderLine} />
        <CartItemDetails
          name={orderLine.name}
          hasWeight={hasWeight}
          displayQuantity={displayQuantity}
          unitLabel={unitLabel}
          options={orderLine.options}
          giftCardId={orderLine.giftCardId}
          giftCardPhoneNumber={giftCardPhoneNumber}
          comment={orderLine.comment}
          dataCy="cart-line"
        />
        <Price orderLine={orderLine} />
        {findMenuItem(orderLine.itemVariantId)?.showComponents === true && componentItems.length > 0 && (
          <div className="col-start-2 col-span-2 gap-2 flex flex-col">
            {componentItems.map((component, index) => (
              <ComponentTreeItem
                key={`${index}-${getComponentKey(component)}`}
                component={component}
                courses={courses}
                findMenuItem={findMenuItem}
                giftCardPhoneNumber={giftCardPhoneNumber}
              />
            ))}
          </div>
        )}
      </div>

      {isActive && <CartItemButtons orderLine={orderLine} />}
    </li>
  );
};

export default React.memo(CompositeCartItem);
